/** formatStubStatus — cross-surface resolver for stub status pill rendering. */

import type { StubStatus } from "./stub-catalog";

export interface StubStatusDisplay {
  label: string;
  pillClass: string;
}

/**
 * Optional translator (D1/W5). When supplied, the label resolves via i18n keys
 * for a trilingual web consumer; when omitted the English strings below are the
 * SSoT (mobile call sites pass nothing → byte-identical, Phase 3).
 */
type TFn = (key: string, params?: Record<string, string | number>) => string;

// Undated roadmap label — mirrors ComingSoonPill's date-free default so no
// surface ever renders a self-falsifying "Coming <quarter>" once that quarter
// arrives. A whenLabel is shown verbatim only when it is a genuinely-future date.
const ROADMAP_LABEL = "In the delivery roadmap";

function roadmapOrWhen(whenLabel: string | undefined, t?: TFn): string {
  if (whenLabel) {
    return t ? t("stubs.comingWhen", { when: whenLabel }) : `Coming ${whenLabel}`;
  }
  // The undated roadmap label already has a shared key (common.deliveryRoadmap).
  return t ? t("common.deliveryRoadmap") : ROADMAP_LABEL;
}

export function formatStubStatus(status: StubStatus, t?: TFn): StubStatusDisplay {
  switch (status.kind) {
    case "live":
      return {
        label: t ? t("stubs.availableNow") : "Available now",
        pillClass: "bg-status-success/10 text-status-success",
      };
    case "next":
      return {
        label: roadmapOrWhen(status.whenLabel, t),
        pillClass: "bg-status-info/10 text-status-info",
      };
    case "future":
      return {
        label: roadmapOrWhen(status.whenLabel, t),
        pillClass: "bg-neutral-100 text-neutral-500",
      };
  }
}
